import React, { useState, useEffect } from 'react';
import { useUser } from '../userContext';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import BeatLoader from 'react-spinners/BeatLoader';
import { TrashIcon, UserPlusIcon, UsersIcon } from '@heroicons/react/24/outline';

// Initialize Firebase functions with the correct region
const functions = getFunctions(undefined, 'europe-west1');

// Initialize the function with the specific name
const createUserAndSendPasswordReset = httpsCallable(functions, 'createUserAndSendPasswordReset');
const deleteUser = httpsCallable(functions, 'deleteUser');

export default function Users() {
  const { user, isOwner, businessProfile, subscriptionType, userProfile } = useUser();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [addingUser, setAddingUser] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState([]);
  const [userLimit, setUserLimit] = useState(0);
  const [showNotification, setShowNotification] = useState(false);
  const [notification, setNotification] = useState({
    type: '', // 'success' | 'error'
    message: ''
  });

  // Fetch users when the component mounts or when businessProfile changes
  const fetchUsers = async () => {
    if (!businessProfile?.id) return;
    try {
      setLoadingUsers(true);
      const usersQuery = query(
        collection(db, 'users'),
        where('companyId', '==', businessProfile.id)
      );
      const querySnapshot = await getDocs(usersQuery);
      const usersList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
      showNotificationMessage('error', 'Failed to load users.');
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchUsers();

    // Set user limit based on subscription type
    if (subscriptionType === 'agency') {
      setUserLimit(25);
    } else {
      setUserLimit(1);
    }
  }, [businessProfile, subscriptionType]);

  const showNotificationMessage = (type, message) => {
    setNotification({ type, message });
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 5000);
  };

  const handleDeleteUser = async (userId) => {
    try {
      setDeletingUserId(userId);
      await deleteUser({ userId });
      showNotificationMessage('success', 'User removed successfully');
      await fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      showNotificationMessage('error', 'Unable to remove user. Please try again.');
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !firstName || !lastName) {
      showNotificationMessage('error', 'All fields are required');
      return;
    }

    try {
      setAddingUser(true);
      const response = await createUserAndSendPasswordReset({
        email,
        firstName,
        lastName,
        companyId: businessProfile.id,
      });

      if (response.data) {
        showNotificationMessage('success', 'Invitation sent! They will receive an email to complete their account setup.');
        setEmail('');
        setFirstName('');
        setLastName('');
        fetchUsers();
      }
    } catch (error) {
      console.error('Error adding user:', error);
      showNotificationMessage('error', 'Unable to add user. Please try again.');
    } finally {
      setAddingUser(false);
    }
  };

  const isImpersonating = businessProfile?.id !== userProfile?.companyId;

  if (isImpersonating) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" style={{fontFamily: '"Share Tech Mono", monospace'}}>
        <div className="rounded-xl p-6" style={{
          background: 'linear-gradient(135deg, #F39C12, #E67E22)',
          border: '2px solid rgba(241, 196, 15, 0.6)',
          boxShadow: '0 4px 12px rgba(243, 156, 18, 0.3)'
        }}>
          <div className="flex items-start gap-3">
            <UsersIcon className="h-6 w-6 text-white flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-black text-white tracking-wide" style={{textShadow: '1px 1px 0px rgba(0,0,0,0.3)'}}>
                NOT APPLICABLE
              </h3>
              <p className="text-white font-bold" style={{textShadow: '1px 1px 0px rgba(0,0,0,0.3)'}}>
                User management is not available while impersonating a client.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" style={{fontFamily: '"Share Tech Mono", monospace'}}>
        <div className="rounded-xl p-6" style={{
          background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
          border: '2px solid rgba(231, 76, 60, 0.6)',
          boxShadow: '0 4px 12px rgba(231, 76, 60, 0.3)'
        }}>
          <div className="flex items-start gap-3">
            <UsersIcon className="h-6 w-6 text-white flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-black text-white tracking-wide" style={{textShadow: '1px 1px 0px rgba(0,0,0,0.3)'}}>
                ACCESS RESTRICTED
              </h3>
              <p className="text-white font-bold" style={{textShadow: '1px 1px 0px rgba(0,0,0,0.3)'}}>
                Only the account administrator can manage users.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" style={{fontFamily: '"Share Tech Mono", monospace'}}>
      {/* Notification */}
      {showNotification && (
        <div 
          className={`mb-6 rounded-xl p-4 transition-all duration-300 ${
            notification.type === 'success' 
              ? 'animate-pulse'
              : ''
          }`}
          style={{
            background: notification.type === 'success' 
              ? 'linear-gradient(135deg, #2ECC71, #27AE60)' 
              : 'linear-gradient(135deg, #E74C3C, #C0392B)',
            border: `2px solid ${notification.type === 'success' ? 'rgba(46, 204, 113, 0.6)' : 'rgba(231, 76, 60, 0.6)'}`,
            boxShadow: `0 4px 12px ${notification.type === 'success' ? 'rgba(46, 204, 113, 0.3)' : 'rgba(231, 76, 60, 0.3)'}`
          }}
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              {notification.type === 'success' ? (
                <svg className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <p className="text-white font-bold" style={{textShadow: '1px 1px 0px rgba(0,0,0,0.3)'}}>
              {notification.message}
            </p>
          </div>
        </div>
      )}

      <div className="space-y-10">
        {/* Header Section */}
        <div className="border-b pb-6" style={{borderColor: 'rgba(241, 196, 15, 0.3)'}}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center mb-1">
                <h2 className="text-base font-black leading-7 tracking-wide" style={{
                  color: '#F1C40F',
                  textShadow: '0 0 4px rgba(241, 196, 15, 0.3)'
                }}>
                  USER MANAGEMENT
                </h2>
                <span className={`ml-2 inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ${
                  users.length >= userLimit 
                    ? 'bg-red-100 text-red-800 border border-red-300' 
                    : 'bg-green-100 text-green-800 border border-green-300'
                }`}>
                  {`${users.length}/${userLimit} USERS`}
                </span>
              </div>
              <p className="text-sm leading-6 font-bold" style={{color: '#BDC3C7'}}>
                Invite team members to access your SmilesFlow Studio account. New users can create and manage campaigns.
              </p>
            </div>
          </div>
        </div>

        {/* Add User Section */}
        <div className="rounded-xl overflow-hidden" style={{
          background: 'linear-gradient(135deg, #2C3E50, #34495E)',
          border: '2px solid rgba(241, 196, 15, 0.4)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
        }}>
          <div className="px-6 py-5 border-b" style={{borderColor: 'rgba(241, 196, 15, 0.3)'}}>
            <div className="flex items-center gap-3">
              <UserPlusIcon className="h-6 w-6" style={{color: '#F1C40F'}} />
              <h2 className="text-lg font-black tracking-wide" style={{
                color: '#F1C40F',
                textShadow: '0 0 4px rgba(241, 196, 15, 0.3)'
              }}>
                INVITE NEW USER
              </h2>
            </div>
            <p className="mt-1 text-sm font-bold" style={{color: '#BDC3C7'}}>
              They will receive an email with instructions to set up their account.
            </p>
          </div>

          <div className="px-6 py-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="block text-sm font-bold" style={{color: '#ECF0F1'}}>
                    FIRST NAME
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                    disabled={addingUser || deletingUserId !== null || users.length >= userLimit}
                    className="block w-full rounded-lg shadow-sm sm:text-sm transition-all duration-200 font-bold py-3 px-4"
                    style={{
                      background: 'rgba(52, 73, 94, 0.6)',
                      border: '1px solid rgba(241, 196, 15, 0.4)',
                      color: '#ECF0F1',
                      fontFamily: '"Share Tech Mono", monospace'
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-bold" style={{color: '#ECF0F1'}}>
                    LAST NAME
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Doe"
                    disabled={addingUser || deletingUserId !== null || users.length >= userLimit}
                    className="block w-full rounded-lg shadow-sm sm:text-sm transition-all duration-200 font-bold py-3 px-4"
                    style={{
                      background: 'rgba(52, 73, 94, 0.6)',
                      border: '1px solid rgba(241, 196, 15, 0.4)',
                      color: '#ECF0F1',
                      fontFamily: '"Share Tech Mono", monospace'
                    }}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-bold" style={{color: '#ECF0F1'}}>
                  EMAIL ADDRESS
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@example.com"
                  disabled={addingUser || deletingUserId !== null || users.length >= userLimit}
                  className="block w-full rounded-lg shadow-sm sm:text-sm transition-all duration-200 font-bold py-3 px-4"
                  style={{
                    background: 'rgba(52, 73, 94, 0.6)',
                    border: '1px solid rgba(241, 196, 15, 0.4)',
                    color: '#ECF0F1',
                    fontFamily: '"Share Tech Mono", monospace'
                  }}
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={!email || !firstName || !lastName || addingUser || deletingUserId !== null || users.length >= userLimit}
                  className={`inline-flex items-center justify-center rounded-lg px-6 py-3 text-sm font-bold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 min-w-[140px] transition-all duration-100 ${
                    email && firstName && lastName && !addingUser && users.length < userLimit
                      ? '' 
                      : 'cursor-not-allowed'
                  }`}
                  style={email && firstName && lastName && !addingUser && users.length < userLimit ? {
                    background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                    border: '2px solid rgba(241, 196, 15, 0.6)',
                    color: '#FFFFFF',
                    textShadow: '1px 1px 0px #A93226'
                  } : {
                    background: 'rgba(52, 73, 94, 0.6)',
                    border: '1px solid rgba(149, 165, 166, 0.3)',
                    color: '#95A5A6'
                  }}
                  onMouseEnter={(e) => {
                    if (email && firstName && lastName && !addingUser && users.length < userLimit) {
                      e.target.style.background = 'linear-gradient(135deg, #C0392B, #A93226)';
                      e.target.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (email && firstName && lastName && !addingUser && users.length < userLimit) {
                      e.target.style.background = 'linear-gradient(135deg, #E74C3C, #C0392B)';
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {addingUser ? (
                    <BeatLoader size={8} color="#ffffff" />
                  ) : (
                    <>
                      <UserPlusIcon className="h-4 w-4 mr-2" />
                      SEND INVITATION
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Current Users Section */}
        <div className="rounded-xl overflow-hidden" style={{
          background: 'linear-gradient(135deg, #2C3E50, #34495E)',
          border: '2px solid rgba(241, 196, 15, 0.4)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
        }}>
          <div className="px-6 py-5 border-b" style={{borderColor: 'rgba(241, 196, 15, 0.3)'}}>
            <div className="flex items-center gap-3">
              <UsersIcon className="h-6 w-6" style={{color: '#F1C40F'}} />
              <h2 className="text-lg font-black tracking-wide" style={{
                color: '#F1C40F',
                textShadow: '0 0 4px rgba(241, 196, 15, 0.3)'
              }}>
                CURRENT USERS
              </h2>
            </div>
            <p className="mt-1 text-sm font-bold" style={{color: '#BDC3C7'}}>
              Users who have access to your company account.
            </p>
          </div>

          <div className="px-6 py-6">
            {loadingUsers ? (
              <div className="flex justify-center items-center py-8">
                <BeatLoader color="#F1C40F" loading={loadingUsers} />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-8">
                <UsersIcon className="h-12 w-12 mx-auto mb-4" style={{color: '#95A5A6'}} />
                <p className="text-lg font-bold" style={{color: '#95A5A6'}}>
                  No users have accepted invitations yet.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {users.map((user) => (
                  <div 
                    key={user.id} 
                    className="flex items-center justify-between p-4 rounded-lg transition-all duration-200"
                    style={{
                      background: 'rgba(52, 73, 94, 0.4)',
                      border: '1px solid rgba(241, 196, 15, 0.3)'
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-black"
                          style={{
                            background: 'linear-gradient(135deg, #F1C40F, #F39C12)',
                            color: '#2C3E50',
                            textShadow: '1px 1px 0px #E67E22'
                          }}
                        >
                          {(user.firstName || user.email || 'U').charAt(0).toUpperCase()}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold" style={{color: '#ECF0F1'}}>
                            {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email}
                          </span>
                          {user.id === businessProfile.ownerId && (
                            <span 
                              className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold"
                              style={{
                                background: 'rgba(46, 204, 113, 0.2)',
                                border: '1px solid rgba(46, 204, 113, 0.4)',
                                color: '#2ECC71'
                              }}
                            >
                              ADMIN
                            </span>
                          )}
                        </div>
                        <p className="text-xs font-bold" style={{color: '#95A5A6'}}>
                          {user.email}
                        </p>
                      </div>
                    </div>
                    {user.id !== businessProfile.ownerId && (
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        disabled={addingUser || deletingUserId !== null}
                        className={`p-2 rounded-lg transition-all duration-200 ${
                          deletingUserId === user.id ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-600/20'
                        }`}
                        style={{
                          border: '1px solid rgba(231, 76, 60, 0.4)',
                          color: '#E74C3C'
                        }}
                        onMouseEnter={(e) => {
                          if (deletingUserId !== user.id) {
                            e.target.style.background = 'rgba(231, 76, 60, 0.1)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (deletingUserId !== user.id) {
                            e.target.style.background = 'transparent';
                          }
                        }}
                      >
                        {deletingUserId === user.id ? (
                          <BeatLoader size={4} color="#E74C3C" />
                        ) : (
                          <TrashIcon className="h-4 w-4" />
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
